import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import { ArrowLeft, Eye, MapPin, Ruler, Footprints, Bookmark, BookmarkCheck, Mail, Play, Pencil } from "lucide-react";

const getEmbedSrc = (v) => {
  if (!v) return null;
  if (v.youtubeId) return `https://www.youtube.com/embed/${v.youtubeId}`;
  const url = v.url || "";
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url || null;
};

const PlayerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isInWatch, toggleWatch, user, loginWithGoogle, isAdmin } = useApp();
  const { toast } = useToast();
  const [activeVideo, setActiveVideo] = useState(0);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/players/${id}`)
      .then(({ data }) => setPlayer(data))
      .catch((err) => {
        const s = err?.response?.status;
        if (s === 401) loginWithGoogle();
        else if (s === 402) navigate("/pricing");
        else if (s === 403) navigate("/onboarding");
        setPlayer(null);
      })
      .finally(() => setLoading(false));
  }, [id, navigate, loginWithGoogle]);

  if (loading) {
    return (
      <div className="pt-32 text-center min-h-screen">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!player) {
    return (
      <div className="pt-32 text-center min-h-screen">
        <p className="text-gray-400">Player not found.</p>
        <Link to="/search" className="text-blue-400 mt-4 inline-block">{t.player.back}</Link>
      </div>
    );
  }

  const watched = isInWatch(player.id);

  const handleWatch = () => {
    if (!user) return loginWithGoogle();
    toggleWatch(player.id);
  };

  const handleContact = () => {
    if (!user) return loginWithGoogle();
    toast({ description: t.player.contactSent });
  };

  const statItems = [
    { label: t.player.appearances, value: player.stats.appearances },
    { label: t.player.goals, value: player.stats.goals },
    { label: t.player.assists, value: player.stats.assists },
    { label: t.player.minutes, value: player.stats.minutes },
  ];
  if (player.stats.cleanSheets !== undefined) {
    statItems.push({ label: t.player.cleanSheets, value: player.stats.cleanSheets });
  }

  const infoItems = [
    { label: t.player.position, value: player.position },
    { label: t.player.age, value: `${player.age} (${player.birthYear})` },
    { label: t.player.height, value: `${player.height}cm` },
    { label: t.player.foot, value: `${player.foot}` },
    { label: t.player.location, value: `${player.city}, ${player.country}` },
    { label: t.player.club, value: player.club },
  ];

  return (
    <div className="pt-16 min-h-screen">
      {/* Header */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-5xl mx-auto px-5 pt-8 pb-10">
          <button onClick={() => navigate("/search")} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-6">
            <ArrowLeft className="w-4 h-4" /> {t.player.back}
          </button>

          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-4xl font-bold text-white font-display shadow-lg shadow-blue-500/20 shrink-0">
              {player.initial}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-3xl font-bold text-white">{player.name}</h1>
                {player.openForTrial && (
                  <span className="text-[11px] font-semibold uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full">
                    {t.player.openTrial}
                  </span>
                )}
              </div>
              <p className="text-blue-400 mt-1 font-medium">{player.position}</p>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-400">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gray-600" /> {player.city}, {player.country}</span>
                <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4 text-gray-600" /> {player.height}cm</span>
                <span className="flex items-center gap-1.5"><Footprints className="w-4 h-4 text-gray-600" /> {player.foot} foot</span>
                <span className="flex items-center gap-1.5"><Eye className="w-4 h-4 text-gray-600" /> {player.views} {t.player.views}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-auto">
              <Button onClick={handleWatch} variant="outline" className={`border-white/15 h-10 ${watched ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" : "text-gray-200 bg-white/5 hover:bg-white/10"}`}>
                {watched ? <BookmarkCheck className="w-4 h-4 mr-2" /> : <Bookmark className="w-4 h-4 mr-2" />}
                {watched ? t.player.removeWatch : t.player.addWatch}
              </Button>
              <Button onClick={handleContact} className="bg-blue-600 hover:bg-blue-500 text-white h-10">
                <Mail className="w-4 h-4 mr-2" /> {t.player.contact}
              </Button>
              {isAdmin && (
                <Button onClick={() => navigate(`/admin/player/${player.id}`)} variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 h-10">
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-5 py-10">
        <Tabs defaultValue="overview">
          <TabsList className="bg-[#0f1116] border border-white/8 p-1 h-auto">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 px-5 py-2">{t.player.overview}</TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 px-5 py-2">{t.player.videos} ({player.videos?.length || 0})</TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400 px-5 py-2">{t.player.stats}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 bg-[#0f1116] border border-white/8 rounded-2xl p-6">
                <p className="text-gray-300 leading-relaxed">{player.bio}</p>
              </div>
              <div className="bg-[#0f1116] border border-white/8 rounded-2xl p-6 space-y-3">
                {infoItems.map((it) => (
                  <div key={it.label} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0 last:pb-0">
                    <span className="text-gray-500">{it.label}</span>
                    <span className="text-gray-200 font-medium">{it.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="videos" className="mt-6">
            {(!player.videos || player.videos.length === 0) ? (
              <div className="text-center py-16 text-gray-500">{t.myprofile.noVideos}</div>
            ) : (
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <div className="aspect-video rounded-2xl overflow-hidden border border-white/8 bg-black">
                  <iframe
                    key={activeVideo}
                    className="w-full h-full"
                    src={getEmbedSrc(player.videos[activeVideo])}
                    title={player.videos[activeVideo].title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <h3 className="text-white font-medium mt-4">{player.videos[activeVideo].title}</h3>
              </div>
              <div className="space-y-3">
                {player.videos.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveVideo(i)}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${i === activeVideo ? "border-blue-500/40 bg-blue-500/10" : "border-white/8 bg-[#0f1116] hover:bg-white/5"}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                      <Play className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-sm text-gray-200">{v.title}</span>
                  </button>
                ))}
              </div>
            </div>
            )}
          </TabsContent>

          <TabsContent value="stats" className="mt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {statItems.map((s) => (
                <div key={s.label} className="bg-[#0f1116] border border-white/8 rounded-2xl p-6 text-center">
                  <div className="font-display text-3xl font-bold text-white">{s.value}</div>
                  <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PlayerProfile;
